import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { fetchCampaignTransferRules, updateCampaignTransferRules } from '../services/transferRule';
import type { UpdateTransferRulePayload } from '../types/transferRule.types';

export const campaignTransferRuleKeys = {
    all: ['smr-admin', 'campaign', 'transfer-rules'] as const,
    forCampaign: (campaignId: string) => [...campaignTransferRuleKeys.all, campaignId] as const,
};

/**
 * Query + mutation for a single campaign's transfer rules. Invalidation is keyed on the
 * CAMPAIGN (never a product key), mirroring useCampaignConfigSettingMutations, so the
 * two-query resolve-then-fetch structure in CampaignTransferRulePanel refetches correctly.
 */
export const useCampaignTransferRules = (campaignId?: string) => {
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: campaignTransferRuleKeys.forCampaign(campaignId ?? 'unresolved'),
        queryFn: () => {
            // The query is gated by enabled below, but narrow here too so the fetch never
            // issues a request to /campaigns/undefined/... if it is ever run unguarded.
            if (!campaignId) {
                throw new Error('useCampaignTransferRules: campaignId is required to fetch transfer rules');
            }
            return fetchCampaignTransferRules(campaignId);
        },
        enabled: Boolean(campaignId),
    });

    const updateMutation = useMutation({
        mutationFn: (payload: UpdateTransferRulePayload) => {
            // Fail fast: the mutation is only reachable in the UI after the panel resolves a
            // campaign, but guard defensively so mutate() can never PUT to /campaigns/undefined/...
            if (!campaignId) {
                throw new Error('useCampaignTransferRules: campaignId is required to update transfer rules');
            }
            return updateCampaignTransferRules(campaignId, payload);
        },
        onSuccess: () => {
            toast.success('Transfer rules saved.');
            // Only invalidate the campaign-scoped key when we actually have a campaignId, so a
            // missing id can never invalidate a .../undefined key.
            if (!campaignId) return;
            queryClient.invalidateQueries({
                queryKey: campaignTransferRuleKeys.forCampaign(campaignId),
            });
        },
    });

    return { query, updateMutation };
};
